import { ComponentType } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, inject, Input, input, OnDestroy, output } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';

import { ApContentService } from '@ap/content';
import { ApDateService } from '@ap/date';
import { ApModalService, ModalClosedReason, ModalContainer, ModalOptions } from '@ap/modal';

import { ApDateInputDirective } from '../date.directive';
import { ApCalendarModalComponent } from '../modal/calendar-modal.component';
import type { ApCalendarView } from '../../date-input.model';

import content from './calendar-control.content.json';

/** Used to generate a unique ID for each datepicker instance. */
let datepickerUid = 0;

/**
 * This is the Calendar Control Component. Component responsible for managing the datepicker popup/dialog.
 * This component needs to be placed in the html, but does not contain any template.
 * It is purely there to coordinate between the {@link ApDateInputDirective} and the {@link ApDatePickerComponent}
 **/
@Component({
    selector: 'ap-calendar-control',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'apCalendarControl'
})
export class ApCalendarControlComponent implements OnDestroy {
    private readonly modalService = inject(ApModalService);
    private readonly contentService = inject(ApContentService);
    private readonly document = inject(DOCUMENT, { optional: true });

    /** The date to open the calendar to initially. */
    @Input()
    get startAt(): Date | null {
        // If an explicit startAt is set we start there, otherwise we start at whatever the currently
        // selected value is.
        return this._startAt || (this._datepickerInput ? this._datepickerInput.value : null);
    }

    set startAt(value: Date | null) {
        this._startAt = ApDateService.getValidDateOrNull(ApDateService.utc(value));
    }

    /** Whether the calendar is open. */
    @Input()
    get opened(): boolean {
        return this._opened;
    }

    set opened(value: boolean) {
        value ? this.open() : this.close();
    }

    /** The currently selected date. */
    get _selected(): Date | null {
        return this.validSelected;
    }

    set _selected(value: Date | null) {
        this.validSelected = value;
    }

    /** The minimum selectable date. */
    get _minDate(): Date | null {
        return this._datepickerInput && this._datepickerInput.min;
    }

    /** The maximum selectable date. */
    get _maxDate(): Date | null {
        return this._datepickerInput && this._datepickerInput.max;
    }

    get _dateFilter(): (date: Date | null) => boolean {
        return this._datepickerInput && this._datepickerInput._dateFilter;
    }
    /** An input indicating the type of the custom header component for the calendar, if set. */
    readonly calendarHeaderComponent = input<ComponentType<any>>(undefined);
    /** The view that the calendar should start in. */
    readonly startView = input<ApCalendarView>('month');
    /**
     * Emits selected year in multiyear view.
     * This doesn't imply a change on the selected date.
     */
    readonly yearSelected = output<Date>();
    /**
     * Emits selected month in year view.
     * This doesn't imply a change on the selected date.
     */
    readonly monthSelected = output<Date>();
    /** Classes to be passed to the datepicker panel. Supports the same syntax as `ngClass`. */
    readonly panelClass = input<string | string[]>(undefined);

    /** The id for the datepicker calendar. */
    id: string;
    /** A reference to the overlay when the calendar is opened as a popup. */
    _popupRef: ModalContainer;
    /** The input element this datepicker is associated with. */
    _datepickerInput: ApDateInputDirective;
    /** Emits new selected date when selected date changes. */
    readonly selectedChanged = new Subject<Date>();
    private validSelected: Date | null = null;
    /** The element that was focused before the datepicker was opened. */
    private focusedElementBeforeOpen: HTMLElement | null = null;
    /** Subscription to value changes in the associated input element. */
    private inputSubscription = Subscription.EMPTY;

    private _startAt: Date | null;

    private _opened = false;

    constructor() {
        const contentService = this.contentService;

        contentService.addContentItems(content);
        datepickerUid++;
        this.id = `ap-date-${datepickerUid}`;
    }

    ngOnDestroy() {
        this.close();
        this.inputSubscription.unsubscribe();

        if (this._popupRef) {
            this._popupRef.close(ModalClosedReason.DECLINED);
        }
    }

    /** Selects the given date */
    _select(date: Date): void {
        const oldValue = this._selected;

        this._selected = date;

        if (!oldValue || (!this._selected && oldValue !== this._selected) || oldValue.toJSON() !== this._selected.toJSON()) {
            this.selectedChanged.next(date);
        }
    }

    /** Emits the selected year in multiyear view */
    _selectYear(normalizedYear: Date): void {
        this.yearSelected.emit(normalizedYear);
    }

    /** Emits selected month in year view */
    _selectMonth(normalizedMonth: Date): void {
        this.monthSelected.emit(normalizedMonth);
    }

    /**
     * Register an input with this datepicker.
     *
     * @param input The datepicker input to register with this datepicker.
     */
    _registerInput(dateInput: ApDateInputDirective): void {
        if (this._datepickerInput) {
            throw Error('An ApDateInput can only be associated with a single input.');
        }
        this._datepickerInput = dateInput;
        this.inputSubscription = this._datepickerInput._valueChange.subscribe((value: Date | null) => (this._selected = value));
    }

    /** Open the calendar. */
    open(): void {
        if (!this._datepickerInput) {
            throw Error('Attempted to open an ApCalendarControl with no associated input.');
        }
        if (this._opened) {
            return;
        }
        if (this.document) {
            this.focusedElementBeforeOpen = this.document.activeElement as HTMLElement;
        }

        this._openAsPopup();
        this._opened = true;
    }

    /** Close the calendar. */
    close(): void {
        if (!this._opened) {
            return;
        }
        if (this._popupRef) {
            this._popupRef.close(ModalClosedReason.SELECTED);
        }

        const completeClose = () => {
            // The `_opened` could've been reset already if
            // we got two events in quick succession.
            if (this._opened) {
                this._opened = false;
                this.focusedElementBeforeOpen = null;
            }
        };

        if (this.focusedElementBeforeOpen && typeof this.focusedElementBeforeOpen.focus === 'function') {
            // Because IE moves focus asynchronously, we can't count on it being restored before we've
            // marked the datepicker as closed. If the event fires out of sequence and the element that
            // we're refocusing opens the datepicker on focus, the user could be stuck with not being
            // able to close the calendar at all. We work around it by making the logic, that marks
            // the datepicker as closed, async as well.
            this.focusedElementBeforeOpen.focus();
            setTimeout(completeClose);
        } else {
            completeClose();
        }
    }

    public getTitle(view: ApCalendarView) {
        return this.contentService.interpolate(this.contentService.get('calendarModal.title'), {
            type: this.contentService.get(`calendarModal.${view}`)
        });
    }

    /** Open the calendar as a popup. */
    private _openAsPopup(): void {
        const modalOptions = new ModalOptions<ApCalendarControlComponent>(this);
        modalOptions.cancelable = true;
        modalOptions.title = this.getTitle(this.startView());
        modalOptions.showHeader = true;
        modalOptions.fullscreen = false;
        modalOptions.classIdentifier = 'ap-modal__datepicker';
        modalOptions.customFocus = true;

        this._popupRef = this.modalService.open(ApCalendarModalComponent, modalOptions);
        this._popupRef.afterClosed().subscribe(() => {
            this._popupRef = undefined;
            this.close();
        });
    }
}
